/**
 * FindingGroupCard - Displays a Phase 3/4/5 multi-photo finding group
 * 
 * Features:
 * - Cover thumbnail with media count badge
 * - Condition badge (Green/Yellow/Red)
 * - Title, summary, procedure outline
 * - Analyze button for pending groups
 * - Phase 4: Generate Notes button & display advisor_summary/technician_notes
 * - Phase 4: Error states for AI failures
 * - Customer decision indicator
 * - Phase 5: Signal-aware badges (Historically Declined, Suggest Monitor)
 */

import { useState } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { 
  Camera, 
  CheckCircle, 
  AlertTriangle, 
  XCircle, 
  Sparkles, 
  Loader2,
  ChevronDown,
  ChevronUp,
  ThumbsUp,
  ThumbsDown,
  Clock,
  ImageIcon,
  MessageSquare,
  FileText,
  AlertCircle,
  RefreshCw,
  TrendingDown,
  Eye,
  Info
} from 'lucide-react';
import { cn } from '@/lib/utils';
import type { FindingGroup } from '@/hooks/useDviFindingGroups';
import type { DviSignalData } from '@/hooks/useDviSignals';
import { getSignalHints } from '@/hooks/useDviSignals';

interface FindingGroupCardProps {
  group: FindingGroup;
  thumbnailUrl?: string | null;
  onAnalyze?: () => void;
  isAnalyzing?: boolean;
  onGenerateNotes?: () => void;
  isGeneratingNotes?: boolean;
  showCustomerDecision?: boolean;
  showTechnicianNotes?: boolean; // Phase 4: role-based display
  compact?: boolean;
  // Phase 5: Signal intelligence
  signalData?: DviSignalData | null;
  onSuggestDowngrade?: () => void;
}

const CONDITION_CONFIG = {
  green: { label: 'Good', icon: CheckCircle, className: 'bg-green-500/10 text-green-600 border-green-500/20' },
  yellow: { label: 'Monitor', icon: AlertTriangle, className: 'bg-yellow-500/10 text-yellow-600 border-yellow-500/20' },
  red: { label: 'Urgent', icon: XCircle, className: 'bg-red-500/10 text-red-600 border-red-500/20' },
};

export function FindingGroupCard({
  group,
  thumbnailUrl,
  onAnalyze,
  isAnalyzing,
  onGenerateNotes,
  isGeneratingNotes,
  showCustomerDecision = false,
  showTechnicianNotes = false,
  compact = false,
  signalData,
  onSuggestDowngrade,
}: FindingGroupCardProps) {
  const [isExpanded, setIsExpanded] = useState(false);
  
  const condition = group.condition;
  const config = condition ? CONDITION_CONFIG[condition] : null;
  const isPending = group.status === 'pending';
  const isAnalyzed = group.status === 'analyzed' || group.status === 'reviewed';
  const isApproved = group.status === 'approved';
  
  const recommendedJobsCount = group.recommended_jobs?.length || 0;
  const totalMinutes = group.recommended_jobs?.reduce((sum, job) => sum + (job.minutes || 0), 0) || 0;
  
  // Phase 4: Note status
  const hasNotes = !!(group.advisor_summary || group.technician_notes);
  const canGenerateNotes = isAnalyzed && !hasNotes && onGenerateNotes;
  
  // Phase 4: Confidence-based warnings
  const isLowConfidence = group.confidence !== null && group.confidence < 0.6;
  const hasNoRecommendations = isAnalyzed && recommendedJobsCount === 0;
  
  // Phase 5: Signal intelligence hints
  const primaryCatalogJobId = group.recommended_jobs?.[0]?.catalog_job_id || null;
  const signalHints = getSignalHints(signalData, condition, group.confidence, primaryCatalogJobId);

  return (
    <Card className={cn(
      'overflow-hidden transition-colors',
      condition === 'red' && 'border-red-200 bg-red-50/30',
      condition === 'yellow' && 'border-yellow-200 bg-yellow-50/30',
      condition === 'green' && 'border-green-200 bg-green-50/30',
    )}>
      <CardContent className={cn('space-y-2', compact ? 'p-2' : 'p-3')}>
        <div className="flex items-start gap-3">
          {/* Thumbnail */}
          <div className="relative w-16 h-16 bg-muted rounded flex items-center justify-center shrink-0 overflow-hidden">
            {thumbnailUrl ? (
              <img 
                src={thumbnailUrl} 
                alt={group.title || 'Finding'} 
                className="w-full h-full object-cover"
              />
            ) : (
              <Camera className="h-6 w-6 text-muted-foreground" />
            )}
            {/* Media count badge */}
            {(group.media_count || 0) > 1 && (
              <div className="absolute bottom-0.5 right-0.5 bg-black/70 text-white text-[10px] px-1 rounded flex items-center gap-0.5">
                <ImageIcon className="h-2.5 w-2.5" />
                {group.media_count}
              </div>
            )}
          </div>

          <div className="flex-1 min-w-0 space-y-1">
            {/* Status Badges */}
            <div className="flex items-center gap-1.5 flex-wrap">
              {/* Condition Badge */}
              {config ? (
                <Badge variant="outline" className={cn('gap-1 text-xs', config.className)}>
                  <config.icon className="h-3 w-3" />
                  {config.label}
                </Badge>
              ) : isPending ? (
                <Badge variant="outline" className="bg-muted text-xs">Pending</Badge>
              ) : null}

              {/* Phase 4: Low confidence warning */}
              {isLowConfidence && (
                <Badge variant="outline" className="bg-yellow-500/10 text-yellow-600 gap-1 text-xs">
                  <AlertTriangle className="h-2.5 w-2.5" />
                  Low Confidence
                </Badge>
              )}

              {/* Phase 5: Historically Declined badge */}
              {signalHints.showHistoricallyDeclined && (
                <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Badge variant="outline" className="bg-orange-500/10 text-orange-600 gap-1 text-xs cursor-help">
                        <TrendingDown className="h-2.5 w-2.5" />
                        Historically Declined
                      </Badge>
                    </TooltipTrigger>
                    <TooltipContent side="top" className="max-w-[200px]">
                      <p className="text-xs">
                        This type of finding is approved only {Math.round((signalHints.historicalApprovalRate || 0) * 100)}% of the time at this shop.
                      </p>
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              )}

              {/* Customer Decision Badge */}
              {showCustomerDecision && group.customer_decision !== 'none' && (
                <Badge 
                  variant="outline" 
                  className={cn(
                    'gap-1 text-xs',
                    group.customer_decision === 'approved' && 'bg-green-500/10 text-green-600',
                    group.customer_decision === 'declined' && 'bg-red-500/10 text-red-600',
                  )}
                >
                  {group.customer_decision === 'approved' ? (
                    <><ThumbsUp className="h-2.5 w-2.5" /> Approved</>
                  ) : (
                    <><ThumbsDown className="h-2.5 w-2.5" /> Declined</>
                  )}
                </Badge>
              )}

              {/* Added to Ticket Badge */}
              {isApproved && (
                <Badge variant="outline" className="bg-green-500/10 text-green-600 text-xs">
                  Added
                </Badge>
              )}

              {/* Phase 4: Notes available badge */}
              {hasNotes && (
                <Badge variant="outline" className="bg-blue-500/10 text-blue-600 gap-1 text-xs">
                  <MessageSquare className="h-2.5 w-2.5" />
                  Notes
                </Badge>
              )}
            </div>

            {/* Title */}
            <p className="font-medium text-sm truncate">
              {group.title || 'Inspection Finding'}
            </p>

            {/* Summary - show advisor_summary if available, else summary */}
            {!compact && (
              <p className="text-xs text-muted-foreground line-clamp-2">
                {group.advisor_summary || group.summary || 'No summary available'}
              </p>
            )}

            {/* Labor Summary */}
            {isAnalyzed && recommendedJobsCount > 0 && (
              <p className="text-xs text-muted-foreground">
                {recommendedJobsCount} job{recommendedJobsCount > 1 ? 's' : ''} • {totalMinutes} min
              </p>
            )}

            {/* Actions */}
            <div className="flex gap-2 pt-1 flex-wrap">
              {isPending && onAnalyze && (
                <Button 
                  size="sm" 
                  variant="secondary" 
                  className="h-7 gap-1 text-xs" 
                  onClick={onAnalyze}
                  disabled={isAnalyzing}
                >
                  {isAnalyzing ? (
                    <Loader2 className="h-3 w-3 animate-spin" />
                  ) : (
                    <Sparkles className="h-3 w-3" />
                  )}
                  Analyze
                </Button>
              )}
              
              {/* Phase 4: Generate Notes button */}
              {canGenerateNotes && (
                <Button 
                  size="sm" 
                  variant="outline" 
                  className="h-7 gap-1 text-xs" 
                  onClick={onGenerateNotes}
                  disabled={isGeneratingNotes}
                >
                  {isGeneratingNotes ? (
                    <Loader2 className="h-3 w-3 animate-spin" />
                  ) : (
                    <FileText className="h-3 w-3" />
                  )}
                  Generate Notes
                </Button>
              )}
            </div>
          </div>
        </div>

        {/* Phase 4: No recommendations info */}
        {hasNoRecommendations && !compact && (
          <Alert className="bg-blue-50/50 border-blue-200">
            <AlertCircle className="h-4 w-4 text-blue-600" />
            <AlertDescription className="text-xs text-blue-700">
              No specific services recommended. This may be a monitoring-only finding.
            </AlertDescription>
          </Alert>
        )}

        {/* Phase 5: Suggest Monitor alert */}
        {signalHints.suggestMonitor && !compact && onSuggestDowngrade && (
          <Alert className="bg-blue-50/50 border-blue-200">
            <Eye className="h-4 w-4 text-blue-600" />
            <AlertDescription className="text-xs text-blue-700 flex items-center justify-between">
              <span>Based on historical patterns, consider "Monitor" status.</span>
              <Button 
                variant="link" 
                size="sm" 
                className="h-auto p-0 text-xs text-blue-700 underline"
                onClick={onSuggestDowngrade}
              >
                Change to Yellow
              </Button>
            </AlertDescription>
          </Alert>
        )}

        {/* Expandable Details */}
        {isAnalyzed && !compact && (group.procedure_outline || recommendedJobsCount > 0 || hasNotes) && (
          <Collapsible open={isExpanded} onOpenChange={setIsExpanded}>
            <CollapsibleTrigger asChild>
              <Button variant="ghost" size="sm" className="w-full h-6 text-xs gap-1">
                {isExpanded ? <ChevronUp className="h-3 w-3" /> : <ChevronDown className="h-3 w-3" />}
                {isExpanded ? 'Hide Details' : 'Show Details'}
              </Button>
            </CollapsibleTrigger>
            <CollapsibleContent className="pt-2 space-y-2">
              {/* Phase 4: Technician Notes (for techs) */}
              {showTechnicianNotes && group.technician_notes && (
                <div className="bg-blue-50/50 border border-blue-200 rounded p-2">
                  <p className="text-xs font-medium text-blue-700 mb-1 flex items-center gap-1">
                    <FileText className="h-3 w-3" />
                    Tech Notes:
                  </p>
                  <p className="text-xs whitespace-pre-line text-blue-900">{group.technician_notes}</p>
                </div>
              )}

              {/* Phase 4: Advisor Summary (for advisors/managers) */}
              {!showTechnicianNotes && group.advisor_summary && (
                <div className="bg-green-50/50 border border-green-200 rounded p-2">
                  <p className="text-xs font-medium text-green-700 mb-1 flex items-center gap-1">
                    <MessageSquare className="h-3 w-3" />
                    Advisor Summary:
                  </p>
                  <p className="text-xs text-green-900">{group.advisor_summary}</p>
                </div>
              )}

              {/* Procedure Outline */}
              {group.procedure_outline && (
                <div className="bg-muted/50 rounded p-2">
                  <p className="text-xs font-medium text-muted-foreground mb-1">What We'll Do:</p>
                  <p className="text-xs whitespace-pre-line">{group.procedure_outline}</p>
                </div>
              )}

              {/* Recommended Jobs */}
              {recommendedJobsCount > 0 && (
                <div>
                  <p className="text-xs font-medium text-muted-foreground mb-1">Recommended Services:</p>
                  <ul className="text-xs space-y-0.5">
                    {group.recommended_jobs.slice(0, 5).map((job, i) => (
                      <li key={i} className="flex justify-between">
                        <span>• {job.notes || `Job ${i + 1}`}</span>
                        <span className="text-muted-foreground">{job.minutes} min</span>
                      </li>
                    ))}
                    {recommendedJobsCount > 5 && (
                      <li className="text-muted-foreground">+{recommendedJobsCount - 5} more</li>
                    )}
                  </ul>
                </div>
              )}

              {/* Parts */}
              {group.recommended_parts?.length > 0 && (
                <div>
                  <p className="text-xs font-medium text-muted-foreground mb-1">Parts (TBD - pricing pending):</p>
                  <ul className="text-xs space-y-0.5">
                    {group.recommended_parts.map((part, i) => (
                      <li key={i}>• {part.name} × {part.quantity}</li>
                    ))}
                  </ul>
                </div>
              )}
            </CollapsibleContent>
          </Collapsible>
        )}
      </CardContent>
    </Card>
  );
}
